import { useEffect, useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { plans } from "@/data/site-content";
import { supabase } from "@/integrations/supabase/client";
import { recordProfile } from "@/hooks/use-session";

/** PayPal hosted-button form config per plan (USD; PayPal converts at checkout). */
const PLAN_PAYPAL_HOSTED_BUTTON: Record<
  string,
  { id: string; style: string }
> = {
  baseline: {
    id: "T4HDJVZQSMFD2",
    style: `.pp-T4HDJVZQSMFD2{text-align:center;border:none;border-radius:0.25rem;min-width:11.625rem;padding:0 2rem;height:2.625rem;font-weight:bold;background-color:#FFD140;color:#000000;font-family:"Helvetica Neue",Arial,sans-serif;font-size:1rem;line-height:1.25rem;cursor:pointer;}`,
  },
  growth: {
    id: "TMGKQRECTPGE8",
    style: `.pp-TMGKQRECTPGE8{text-align:center;border:none;border-radius:0.25rem;min-width:11.625rem;padding:0 2rem;height:2.625rem;font-weight:bold;background-color:#FFD140;color:#000000;font-family:"Helvetica Neue",Arial,sans-serif;font-size:1rem;line-height:1.25rem;cursor:pointer;}`,
  },
};

const PAYPAL_SDK_URL =
  "https://www.paypal.com/sdk/js?client-id=BAAKv06pnc_1vj_Mcx7DcEe9QDpVzdM03V7CU2gtCzo7_slB1lr3PHfqm9E_kq-0C4lrAEssSuL51O-4nI&components=hosted-buttons&disable-funding=venmo&currency=USD";

function PayPalHostedForm({
  hostedButton,
  onSubmit,
}: {
  hostedButton: { id: string; style: string };
  onSubmit?: () => void;
}) {
  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: hostedButton.style }} />
      <form
        action={`https://www.paypal.com/ncp/payment/${hostedButton.id}`}
        method="post"
        target="_blank"
        style={{ display: "inline-grid", justifyItems: "center", alignContent: "start", gap: "0.5rem" }}
        onSubmit={onSubmit}
      >
        <input
          className={`pp-${hostedButton.id}`}
          type="submit"
          value="Pay Now"
        />
        <img
          src="https://www.paypalobjects.com/images/Debit_Credit_APM.svg"
          alt="Cards accepted"
        />
        <section style={{ fontSize: "0.75rem" }}>
          {" "}
          Powered by{" "}
          <img
            src="https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-wordmark-color.svg"
            alt="PayPal"
            style={{ height: "0.875rem", verticalAlign: "middle" }}
          />
        </section>
      </form>
    </div>
  );
}

type CheckoutSearch = { plan: string; mode: "monthly" | "once" };

export const Route = createFileRoute("/checkout")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>): CheckoutSearch => ({
    plan: typeof search["plan"] === "string" ? search["plan"] : "growth",
    mode: search["mode"] === "once" ? "once" : "monthly",
  }),
  beforeLoad: async ({ search, location }) => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      throw redirect({ to: "/auth", search: { next: location.href } });
    }
    void recordProfile(data.user);
    return { userEmail: data.user.email ?? "", userId: data.user.id, search };
  },
  head: () => ({
    meta: [
      { title: "Secure Checkout | MG Content Labs" },
      {
        name: "description",
        content:
          "Complete your MG Content Labs plan purchase securely with PayPal. Pay monthly or as a one-time payment.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Secure Checkout | MG Content Labs" },
      { property: "og:description", content: "Complete your digital marketing plan purchase securely with PayPal." },
    ],
    scripts: [{ src: PAYPAL_SDK_URL, async: true }],
  }),
  component: CheckoutPage,
});

function CheckoutPage() {
  const { plan: planSlug, mode } = Route.useSearch();
  const { userEmail, userId } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const [confirmed, setConfirmed] = useState(false);

  const plan = plans.find((p) => p.slug === planSlug && p.ctaKind === "checkout");
  const hostedButton = plan ? PLAN_PAYPAL_HOSTED_BUTTON[plan.slug] : undefined;


  if (!plan) {
    return (
      <div className="min-h-screen bg-white text-brand-deep">
        <SiteNav />
        <div className="mx-auto max-w-2xl px-6 py-32 text-center">
          <h1 className="font-serif text-4xl">Plan not available</h1>
          <p className="mt-4 text-brand-deep/70">
            Choose a plan from our pricing page, or contact us for a custom scope.
          </p>
          <Link to="/pricing" className="mt-8 inline-block bg-brand-accent px-8 py-4 text-sm font-bold uppercase tracking-widest text-white">
            View pricing
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />

      <main className="mx-auto max-w-5xl px-6 py-16 md:py-24">
        <h1 className="font-serif text-4xl md:text-5xl">
          Checkout — <span className="italic text-brand-accent">{plan.name}</span>
        </h1>
        <p className="mt-4 max-w-xl text-brand-deep/70">{plan.tagline}</p>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_400px]">
          {/* Summary */}
          <section className="border border-brand-deep/10 p-8">
            <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              What&rsquo;s included
            </h2>
            <ul className="mt-6 space-y-3 text-sm text-brand-deep/75">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="mt-0.5 text-brand-accent">✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 border-t border-brand-deep/10 pt-6">
              <div className="flex items-baseline justify-between">
                <span className="text-sm uppercase tracking-widest text-brand-deep/50">
                  {mode === "monthly" ? "Billed monthly" : "One-time payment"}
                </span>
                <span className="font-serif text-3xl italic tabular-nums">
                  {plan.price.inr}
                  {mode === "monthly" && <span className="text-base not-italic">/mo</span>}
                </span>
              </div>
              <p className="mt-2 text-xs text-brand-deep/50">
                Approx. {plan.price.usd}
                {mode === "monthly" ? "/month" : ""} · processed securely via PayPal
              </p>
            </div>
          </section>

          {/* Payment */}
          <section className="h-fit border border-brand-deep/10 bg-brand-muted p-8">
            <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Payment
            </h2>
            <p className="mt-2 text-xs text-brand-deep/60">Signed in as {userEmail}</p>


            <div className="mt-5 grid grid-cols-2 gap-2 rounded-full bg-white p-1 text-xs font-bold uppercase tracking-widest">
              <button
                type="button"
                onClick={() => {
                  setConfirmed(false);
                  navigate({ search: { plan: plan.slug, mode: "monthly" } });
                }}
                className={`rounded-full py-2.5 transition ${mode === "monthly" ? "bg-brand-accent text-white" : "text-brand-deep/60 hover:text-brand-accent"}`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmed(false);
                  navigate({ search: { plan: plan.slug, mode: "once" } });
                }}
                className={`rounded-full py-2.5 transition ${mode === "once" ? "bg-brand-accent text-white" : "text-brand-deep/60 hover:text-brand-accent"}`}
              >
                One-time
              </button>
            </div>

            <div className="mt-6 min-h-[180px]">
              {hostedButton ? (
                <>
                  <p className="text-sm text-brand-deep/70">
                    Pay <strong className="tabular-nums">{plan.price.usd}</strong> (approx.{" "}
                    {plan.price.inr}) securely with the PayPal button below.
                    {mode === "monthly" &&
                      " For monthly plans, pay the same amount each month — cancel anytime."}
                  </p>
                  <div className="mt-5 flex justify-center">
                    <PayPalHostedForm
                      hostedButton={hostedButton}
                      onSubmit={() => {
                        setConfirmed(true);
                        void supabase
                          .from("profiles")
                          .update({
                            plan_slug: plan.slug,
                            plan_mode: mode,
                            plan_selected_at: new Date().toISOString(),
                          })
                          .eq("id", userId);
                      }}
                    />
                  </div>
                  {confirmed && (
                    <div className="mt-5 border border-brand-accent/40 bg-white p-4 text-sm text-brand-deep">
                      Thank you! Once your payment of <strong>{plan.price.inr}</strong> (
                      {plan.price.usd}) arrives, your editor will email you within one business
                      day to kick off onboarding.
                    </div>
                  )}
                  <p className="mt-3 text-center text-xs text-brand-deep/50">
                    Cards, PayPal balance and UPI accepted where enabled. Charged in USD; PayPal converts to your local currency at checkout.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-brand-deep/70">
                    You&rsquo;ll be taken to PayPal to pay{" "}
                    <strong className="tabular-nums">{plan.price.usd}</strong>{" "}
                    (approx. {plan.price.inr}) securely.
                    {mode === "monthly" &&
                      " For monthly plans, pay the same amount each month — cancel anytime."}
                  </p>
                  <p className="mt-5 text-sm text-red-700">
                    The secure payment button is not configured for this plan. Please contact support.
                  </p>
                </>
              )}
            </div>

            <p className="mt-6 text-[10px] uppercase tracking-widest text-brand-deep/80">
              Secured by PayPal · Cards accepted · Cancel anytime
            </p>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
